import { IconButton, Link, Tooltip } from '@lego/klik-ui';
import {
  StatusErrorBold,
  StatusInprogress,
  StatusSuccessBold,
  StatusWarningBold,
  Trash,
} from '@lego/klik-ui/icons';
import type { MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { UserNameLink } from '@/components/common/UserNameLink';
import type { DeviceNetworkConfigResponse } from '@/hooks/device';
import { dateTimeFormat } from '@/utility/date';
import { createColumnHelper } from '../appTable';

export type EnrichedDeviceNetworkConfig = DeviceNetworkConfigResponse & {
  label?: string;
  fileName?: string;
  connectionName?: string;
  connectionType?: string;
  interfaceName?: string;
};

const columnHelper = createColumnHelper<EnrichedDeviceNetworkConfig>();

const statusConfig = {
  APPLIED: { icon: StatusSuccessBold, color: 'green', label: 'Applied' },
  IN_PROGRESS: { icon: StatusInprogress, color: 'grey', label: 'In Progress' },
  FAILED: { icon: StatusErrorBold, color: 'red', label: 'Failed' },
  PENDING_REMOVAL: { icon: StatusWarningBold, color: 'yellow.500', label: 'Pending Removal' },
} as const;

export const deviceNetworkConfigColumns = (
  onUnassign?: (networkConfigId: number) => Promise<void>,
  getManagePath?: (networkConfigId: number) => string
) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      header: 'ID',
      size: 80,
    }),
    columnHelper.accessor('networkConfigId', {
      filterFn: 'weakEquals',
      header: 'Config ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
      size: 100,
    }),
    columnHelper.accessor('label', {
      header: 'Label',
      cell: (info) =>
        getManagePath ? (
          <Link
            as={RouterLink}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
            p={0}
            to={getManagePath(info.row.original.networkConfigId)}
          >
            {info.getValue() ?? `Config #${info.row.original.networkConfigId}`}
          </Link>
        ) : (
          (info.getValue() ?? '—')
        ),
    }),
    columnHelper.accessor('fileName', {
      header: 'File Name',
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.accessor('connectionName', {
      header: 'Connection Name',
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.accessor('interfaceName', {
      header: 'Interface',
      size: 120,
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.accessor('connectionType', {
      header: 'Type',
      size: 100,
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      size: 120,
      cell: (info) => {
        const status = info.getValue();
        const config = statusConfig[status];
        const StatusIcon = config.icon;
        const failureReason = info.row.original.failureReason;
        const tooltipLabel =
          status === 'FAILED' && failureReason ? `${config.label}: ${failureReason}` : config.label;

        return (
          <Tooltip label={tooltipLabel}>
            <StatusIcon color={config.color} />
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor('createdByOid', {
      header: 'Assigned By',
      enableSorting: false,
      cell: (info) => <UserNameLink oid={info.getValue()} />,
    }),
    columnHelper.accessor('createdAt', {
      header: 'Assigned At',
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
    }),
    columnHelper.accessor('updatedByOid', {
      header: 'Updated By',
      enableSorting: false,
      cell: (info) => <UserNameLink oid={info.getValue()} />,
    }),
    columnHelper.accessor('updatedAt', {
      header: 'Updated At',
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <Tooltip label="Unassign network config from device">
          <IconButton
            aria-label="Unassign network config"
            icon={<Trash />}
            isDisabled={!onUnassign}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              void onUnassign?.(row.original.networkConfigId);
            }}
            size="sm"
            variant="ghost"
          />
        </Tooltip>
      ),
    }),
  ]);
